import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Badge, Chip } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import { getMyMoods } from '../services/api';

export default function NotificationBell({ streaks = [], moodSummary = null }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('imara_dismissed_notifs') || '[]');
    } catch { return []; }
  });
  const [todayMoodLogged, setTodayMoodLogged] = useState(true);
  const dropdownRef = useRef(null);

  useEffect(() => {
    checkTodayMood();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const checkTodayMood = async () => {
    try {
      const res = await getMyMoods();
      const moods = res.data;
      const today = new Date().toDateString();
      const loggedToday = moods.some(
        (m) => new Date(m.created_at).toDateString() === today
      );
      setTodayMoodLogged(loggedToday);
    } catch (e) {}
  };

  const today = new Date().toDateString();
  const thisWeek = `${new Date().getFullYear()}-W${Math.ceil(new Date().getDate() / 7)}`;

  // Generate notifications from data
  const allNotifications = [];

  // 1. Mood not logged today
  if (!todayMoodLogged) {
    allNotifications.push({
      id: `mood_today_${today}`,
      emoji: '📊',
      message: "You haven't logged your mood today.",
      action: 'Log it now →',
      path: '/mood',
      color: '#ec4899',
    });
  }

  // 2. Streak milestones
  const milestones = [3, 7, 14, 21, 30, 60, 100];
  streaks.forEach((streak) => {
    const hit = milestones.find((m) => streak.current_count === m);
    if (hit) {
      allNotifications.push({
        id: `milestone_${streak.id}_${hit}`,
        emoji: '🎉',
        message: `${hit}-day ${streak.streak_type} streak! You're on fire.`,
        action: 'View Streaks →',
        path: '/streaks',
        color: '#f97316',
      });
    }
  });

  // 3. Streak daily reminder
  streaks.forEach((streak) => {
    if (streak.current_count > 0 && !milestones.includes(streak.current_count)) {
      allNotifications.push({
        id: `streak_reminder_${streak.id}_${today}`,
        emoji: '🔥',
        message: `Your ${streak.streak_type} streak is at ${streak.current_count} days — don't break it!`,
        action: 'Check in →',
        path: '/streaks',
        color: '#f97316',
      });
    }
  });

  // 4. Low mood nudge
  if (moodSummary && moodSummary.average_score < 2.5) {
    allNotifications.push({
      id: `mood_low_${thisWeek}`,
      emoji: '🌿',
      message: "Your mood has been low lately. A breathing session might help.",
      action: 'Try Breathe →',
      path: '/breathing',
      color: '#22d3ee',
    });
  }

  // 5. Great mood celebration
  if (moodSummary && moodSummary.average_score >= 4.2) {
    allNotifications.push({
      id: `mood_great_${thisWeek}`,
      emoji: '😊',
      message: "You're on a great run! Average mood is high this week.",
      action: null,
      path: null,
      color: '#4ade80',
    });
  }

  // Filter out dismissed
  const active = allNotifications.filter((n) => !dismissed.includes(n.id));

  const dismiss = (id) => {
    const updated = [...dismissed, id];
    setDismissed(updated);
    localStorage.setItem('imara_dismissed_notifs', JSON.stringify(updated));
  };

  const dismissAll = () => {
    const ids = active.map((n) => n.id);
    const updated = [...dismissed, ...ids];
    setDismissed(updated);
    localStorage.setItem('imara_dismissed_notifs', JSON.stringify(updated));
    setOpen(false);
  };

  return (
    <Box sx={{ position: 'relative' }} ref={dropdownRef}>
      <IconButton
        onClick={() => setOpen((o) => !o)}
        sx={{ color: active.length > 0 ? '#4ade80' : 'rgba(255,255,255,0.3)', p: 0.5 }}
      >
        <Badge
          badgeContent={active.length}
          sx={{
            '& .MuiBadge-badge': {
              background: '#ef4444',
              color: 'white',
              fontSize: '0.65rem',
              minWidth: 16,
              height: 16,
            }
          }}
        >
          <NotificationsIcon fontSize="small" />
        </Badge>
      </IconButton>

      {open && (
        <Box sx={{
          position: 'absolute',
          top: 36,
          right: 0,
          width: 300,
          background: '#0d1f3c',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          zIndex: 1000,
          overflow: 'hidden',
        }}>
          {/* Header */}
          <Box sx={{
            px: 2, py: 1.5,
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <Typography sx={{ color: '#e8f4f8', fontWeight: 700, fontSize: '0.85rem' }}>
              Notifications
            </Typography>
            {active.length > 0 && (
              <Typography
                onClick={dismissAll}
                sx={{ color: '#7fa8c8', fontSize: '0.72rem', cursor: 'pointer', '&:hover': { color: '#22d3ee' } }}
              >
                Clear all
              </Typography>
            )}
          </Box>

          {/* Notification list */}
          {active.length === 0 ? (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>✅</Typography>
              <Typography sx={{ color: '#7fa8c8', fontSize: '0.82rem' }}>
                You're all caught up!
              </Typography>
            </Box>
          ) : (
            active.map((notif) => (
              <Box
                key={notif.id}
                sx={{
                  px: 2, py: 1.5,
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                  display: 'flex', gap: 1.5, alignItems: 'flex-start',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Typography sx={{ fontSize: '1.1rem', mt: 0.2 }}>{notif.emoji}</Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ color: '#e8f4f8', fontSize: '0.8rem', lineHeight: 1.5, mb: 0.5 }}>
                    {notif.message}
                  </Typography>
                  {notif.action && (
                    <Chip
                      label={notif.action}
                      size="small"
                      onClick={() => { setOpen(false); navigate(notif.path); }}
                      sx={{
                        background: `rgba(${hexToRgb(notif.color)}, 0.15)`,
                        color: notif.color,
                        fontSize: '0.68rem',
                        height: 20,
                        cursor: 'pointer',
                        border: `1px solid rgba(${hexToRgb(notif.color)}, 0.3)`,
                        '&:hover': { background: `rgba(${hexToRgb(notif.color)}, 0.25)` },
                      }}
                    />
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => dismiss(notif.id)}
                  sx={{ color: 'rgba(255,255,255,0.2)', p: 0.2, mt: 0.2, '&:hover': { color: 'rgba(255,255,255,0.5)' } }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
}

function hexToRgb(hex) {
  const c = hex.replace('#', '');
  return `${parseInt(c.slice(0,2),16)}, ${parseInt(c.slice(2,4),16)}, ${parseInt(c.slice(4,6),16)}`;
}